import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  MaintenanceStatusCategory,
  MaintenanceStatusSubCategory,
  MaintenanceStatusWithCategories,
  CreateMaintenanceStatusCategoryRequest,
  CreateMaintenanceStatusSubCategoryRequest,
  UpdateMaintenanceStatusCategoryRequest,
  UpdateMaintenanceStatusSubCategoryRequest,
  MaintenanceStatusSearchResult,
  MaintenanceStatusStats
} from '../types/maintenanceStatus';
import { isLocalMode, getLocalList, setLocalList, generateLocalId } from '../utils/localModeHelper';

const STATUS_CATEGORIES_COLLECTION = 'maintenance_status_categories';
const STATUS_SUB_CATEGORIES_COLLECTION = 'maintenance_status_sub_categories';
const CATEGORIES_LS_KEY = 'maintenance_status_categories_local';
const SUB_CATEGORIES_LS_KEY = 'maintenance_status_sub_categories_local';

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val instanceof Timestamp) return val.toDate();
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

export class MaintenanceStatusCategoryService {
  static async getCategories(branchId?: string): Promise<MaintenanceStatusCategory[]> {
    try {
      let list: MaintenanceStatusCategory[];

      if (isLocalMode()) {
        list = getLocalList<MaintenanceStatusCategory>(CATEGORIES_LS_KEY);
      } else {
        let q = query(collection(db, STATUS_CATEGORIES_COLLECTION));
        if (branchId) q = query(q, where('branchId', '==', branchId));
        const snapshot = await getDocs(q);
        list = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: toDate(doc.data().createdAt),
          updatedAt: toDate(doc.data().updatedAt)
        })) as MaintenanceStatusCategory[];
      }

      if (branchId) list = list.filter(c => !c.branchId || c.branchId === branchId);
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      return list;
    } catch (error: any) {
      console.error('❌ خطأ في جلب فئات حالات الصيانة:', error);
      throw new Error(`فشل في جلب فئات حالات الصيانة: ${error.message || error.code || 'خطأ غير معروف'}`);
    }
  }

  static async addCategory(request: CreateMaintenanceStatusCategoryRequest, userId: string): Promise<MaintenanceStatusCategory> {
    try {
      const now = new Date();
      const categoryData: MaintenanceStatusCategory = {
        id: '',
        name: request.name,
        description: request.description,
        icon: request.icon,
        color: request.color,
        order: request.order || 0,
        isActive: true,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        branchId: request.branchId
      };

      if (isLocalMode()) {
        const newCategory: MaintenanceStatusCategory = { ...categoryData, id: generateLocalId('status_cat') };
        const list = getLocalList<MaintenanceStatusCategory>(CATEGORIES_LS_KEY);
        list.push(newCategory);
        setLocalList(CATEGORIES_LS_KEY, list);
        return newCategory;
      }

      const docRef = await addDoc(collection(db, STATUS_CATEGORIES_COLLECTION), {
        ...request,
        order: request.order || 0,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      });

      return { ...categoryData, id: docRef.id };
    } catch (error: any) {
      console.error('❌ خطأ في إضافة فئة حالة صيانة:', error);
      throw new Error(`فشل في إضافة فئة حالة صيانة: ${error.message}`);
    }
  }

  static async updateCategory(id: string, request: UpdateMaintenanceStatusCategoryRequest): Promise<void> {
    try {
      if (isLocalMode()) {
        const list = getLocalList<MaintenanceStatusCategory>(CATEGORIES_LS_KEY);
        const idx = list.findIndex(c => c.id === id);
        if (idx >= 0) {
          list[idx] = { ...list[idx], ...request, updatedAt: new Date() };
          setLocalList(CATEGORIES_LS_KEY, list);
        }
        return;
      }

      await updateDoc(doc(db, STATUS_CATEGORIES_COLLECTION, id), {
        ...request,
        updatedAt: serverTimestamp()
      });
    } catch (error: any) {
      console.error('❌ خطأ في تحديث فئة حالة صيانة:', error);
      throw new Error(`فشل في تحديث فئة حالة صيانة: ${error.message}`);
    }
  }

  static async deleteCategory(id: string): Promise<void> {
    try {
      const subCategories = await MaintenanceStatusSubCategoryService.getSubCategoriesByCategoryId(id);
      if (subCategories.length > 0) {
        throw new Error('لا يمكن حذف الفئة لوجود فئات فرعية مرتبطة بها');
      }

      if (isLocalMode()) {
        const list = getLocalList<MaintenanceStatusCategory>(CATEGORIES_LS_KEY).filter(c => c.id !== id);
        setLocalList(CATEGORIES_LS_KEY, list);
        return;
      }

      await deleteDoc(doc(db, STATUS_CATEGORIES_COLLECTION, id));
    } catch (error: any) {
      console.error('❌ خطأ في حذف فئة حالة صيانة:', error);
      throw new Error(`فشل في حذف فئة حالة صيانة: ${error.message}`);
    }
  }

  static async getCategory(id: string): Promise<MaintenanceStatusCategory | null> {
    try {
      if (isLocalMode()) {
        return getLocalList<MaintenanceStatusCategory>(CATEGORIES_LS_KEY).find(c => c.id === id) || null;
      }

      const docSnap = await getDoc(doc(db, STATUS_CATEGORIES_COLLECTION, id));
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt)
        } as MaintenanceStatusCategory;
      }
      return null;
    } catch (error: any) {
      console.error('❌ خطأ في جلب فئة حالة صيانة:', error);
      throw new Error(`فشل في جلب فئة حالة صيانة: ${error.message}`);
    }
  }
}

export class MaintenanceStatusSubCategoryService {
  static async getSubCategories(branchId?: string): Promise<MaintenanceStatusSubCategory[]> {
    try {
      let list: MaintenanceStatusSubCategory[];

      if (isLocalMode()) {
        list = getLocalList<MaintenanceStatusSubCategory>(SUB_CATEGORIES_LS_KEY);
      } else {
        let q = query(collection(db, STATUS_SUB_CATEGORIES_COLLECTION));
        if (branchId) q = query(q, where('branchId', '==', branchId));
        const snapshot = await getDocs(q);
        list = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: toDate(doc.data().createdAt),
          updatedAt: toDate(doc.data().updatedAt)
        })) as MaintenanceStatusSubCategory[];
      }

      if (branchId) list = list.filter(c => !c.branchId || c.branchId === branchId);
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      return list;
    } catch (error: any) {
      console.error('❌ خطأ في جلب الفئات الفرعية:', error);
      throw new Error(`فشل في جلب الفئات الفرعية: ${error.message || error.code || 'خطأ غير معروف'}`);
    }
  }

  static async getSubCategoriesByCategoryId(categoryId: string): Promise<MaintenanceStatusSubCategory[]> {
    try {
      const list = await this.getSubCategories();
      return list.filter(s => s.categoryId === categoryId);
    } catch (error: any) {
      console.error('❌ خطأ في جلب الفئات الفرعية:', error);
      throw new Error(`فشل في جلب الفئات الفرعية: ${error.message}`);
    }
  }

  static async addSubCategory(request: CreateMaintenanceStatusSubCategoryRequest, userId: string): Promise<MaintenanceStatusSubCategory> {
    try {
      const now = new Date();
      const subData: MaintenanceStatusSubCategory = {
        id: '',
        name: request.name,
        description: request.description,
        icon: request.icon,
        color: request.color,
        categoryId: request.categoryId,
        order: request.order || 0,
        isActive: true,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        branchId: request.branchId
      };

      if (isLocalMode()) {
        const newSub: MaintenanceStatusSubCategory = { ...subData, id: generateLocalId('status_sub') };
        const list = getLocalList<MaintenanceStatusSubCategory>(SUB_CATEGORIES_LS_KEY);
        list.push(newSub);
        setLocalList(SUB_CATEGORIES_LS_KEY, list);
        return newSub;
      }

      const docRef = await addDoc(collection(db, STATUS_SUB_CATEGORIES_COLLECTION), {
        ...request,
        order: request.order || 0,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      });

      return { ...subData, id: docRef.id };
    } catch (error: any) {
      console.error('❌ خطأ في إضافة فئة فرعية:', error);
      throw new Error(`فشل في إضافة فئة فرعية: ${error.message}`);
    }
  }

  static async updateSubCategory(id: string, request: UpdateMaintenanceStatusSubCategoryRequest): Promise<void> {
    try {
      if (isLocalMode()) {
        const list = getLocalList<MaintenanceStatusSubCategory>(SUB_CATEGORIES_LS_KEY);
        const idx = list.findIndex(c => c.id === id);
        if (idx >= 0) {
          list[idx] = { ...list[idx], ...request, updatedAt: new Date() };
          setLocalList(SUB_CATEGORIES_LS_KEY, list);
        }
        return;
      }

      await updateDoc(doc(db, STATUS_SUB_CATEGORIES_COLLECTION, id), {
        ...request,
        updatedAt: serverTimestamp()
      });
    } catch (error: any) {
      console.error('❌ خطأ في تحديث فئة فرعية:', error);
      throw new Error(`فشل في تحديث فئة فرعية: ${error.message}`);
    }
  }

  static async deleteSubCategory(id: string): Promise<void> {
    try {
      if (isLocalMode()) {
        const list = getLocalList<MaintenanceStatusSubCategory>(SUB_CATEGORIES_LS_KEY).filter(c => c.id !== id);
        setLocalList(SUB_CATEGORIES_LS_KEY, list);
        return;
      }

      await deleteDoc(doc(db, STATUS_SUB_CATEGORIES_COLLECTION, id));
    } catch (error: any) {
      console.error('❌ خطأ في حذف فئة فرعية:', error);
      throw new Error(`فشل في حذف فئة فرعية: ${error.message}`);
    }
  }

  static async getSubCategory(id: string): Promise<MaintenanceStatusSubCategory | null> {
    try {
      if (isLocalMode()) {
        return getLocalList<MaintenanceStatusSubCategory>(SUB_CATEGORIES_LS_KEY).find(c => c.id === id) || null;
      }

      const docSnap = await getDoc(doc(db, STATUS_SUB_CATEGORIES_COLLECTION, id));
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt)
        } as MaintenanceStatusSubCategory;
      }
      return null;
    } catch (error: any) {
      console.error('❌ خطأ في جلب فئة فرعية:', error);
      throw new Error(`فشل في جلب فئة فرعية: ${error.message}`);
    }
  }
}

export class MaintenanceStatusService {
  static async getAllStatusesWithCategories(branchId?: string): Promise<MaintenanceStatusWithCategories[]> {
    try {
      const [categories, subCategories] = await Promise.all([
        MaintenanceStatusCategoryService.getCategories(branchId),
        MaintenanceStatusSubCategoryService.getSubCategories(branchId)
      ]);

      const statuses: MaintenanceStatusWithCategories[] = [];

      categories.forEach(category => {
        statuses.push({
          id: category.id,
          categoryId: category.id,
          categoryName: category.name,
          name: category.name,
          description: category.description,
          icon: category.icon,
          color: category.color,
          order: category.order,
          isActive: category.isActive,
          createdAt: category.createdAt,
          updatedAt: category.updatedAt,
          createdBy: category.createdBy,
          branchId: category.branchId
        });
      });

      subCategories.forEach(subCategory => {
        const parentCategory = categories.find(cat => cat.id === subCategory.categoryId);
        statuses.push({
          id: subCategory.id,
          categoryId: subCategory.categoryId,
          subCategoryId: subCategory.id,
          categoryName: parentCategory?.name || 'غير محدد',
          subCategoryName: subCategory.name,
          name: subCategory.name,
          description: subCategory.description,
          icon: subCategory.icon,
          color: subCategory.color,
          order: subCategory.order,
          isActive: subCategory.isActive,
          createdAt: subCategory.createdAt,
          updatedAt: subCategory.updatedAt,
          createdBy: subCategory.createdBy,
          branchId: subCategory.branchId
        });
      });

      statuses.sort((a, b) => a.order - b.order);
      return statuses;
    } catch (error: any) {
      console.error('❌ خطأ في جلب حالات الصيانة مع الفئات:', error);
      throw new Error(`فشل في جلب حالات الصيانة مع الفئات: ${error.message}`);
    }
  }

  static async searchStatuses(searchTerm: string, branchId?: string): Promise<MaintenanceStatusSearchResult> {
    try {
      const [categories, subCategories] = await Promise.all([
        MaintenanceStatusCategoryService.getCategories(branchId),
        MaintenanceStatusSubCategoryService.getSubCategories(branchId)
      ]);

      const term = searchTerm.toLowerCase();
      const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(term) ||
        (category.description || '').toLowerCase().includes(term)
      );

      const filteredSubCategories = subCategories.filter(subCategory =>
        subCategory.name.toLowerCase().includes(term) ||
        (subCategory.description || '').toLowerCase().includes(term)
      );

      return {
        categories: filteredCategories,
        subCategories: filteredSubCategories,
        total: filteredCategories.length + filteredSubCategories.length
      };
    } catch (error: any) {
      console.error('❌ خطأ في البحث في حالات الصيانة:', error);
      throw new Error(`فشل في البحث في حالات الصيانة: ${error.message}`);
    }
  }

  static async getStats(branchId?: string): Promise<MaintenanceStatusStats> {
    try {
      const [categories, subCategories] = await Promise.all([
        MaintenanceStatusCategoryService.getCategories(branchId),
        MaintenanceStatusSubCategoryService.getSubCategories(branchId)
      ]);

      const activeCategories = categories.filter(cat => cat.isActive).length;
      const activeSubCategories = subCategories.filter(subCat => subCat.isActive).length;

      const categoriesByBranch: Record<string, number> = {};
      categories.forEach(category => {
        const branch = category.branchId || 'عام';
        categoriesByBranch[branch] = (categoriesByBranch[branch] || 0) + 1;
      });

      return {
        totalCategories: categories.length,
        totalSubCategories: subCategories.length,
        activeCategories,
        activeSubCategories,
        categoriesByBranch
      };
    } catch (error: any) {
      console.error('❌ خطأ في جلب إحصائيات حالات الصيانة:', error);
      throw new Error(`فشل في جلب إحصائيات حالات الصيانة: ${error.message}`);
    }
  }
}
